/* eslint-disable max-classes-per-file */
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { buildExportFilename } from "../modules/shared/utils/export-helpers";
import { IStartEndDate } from "./app.service";
import { AuthService } from "./auth.service";
import { NameValue } from "./http.service";
import { IAgent, ICollateral, PolicyLetter } from "./loan.service";

//
// Updated list per 697 / 705
//
// Still should probably be coming from database for consistency
//
// ClaimStatusEnum below is still useful typing; not sure how to work around having that duplication
// while retaining good intellisense on the angular side
//

export const ClaimStatuses = [
    { claimStatusCode: "a", isOpen: true, label: "Submitted" },
    { claimStatusCode: "aa", isOpen: false, label: "Claim Denied" },
    { claimStatusCode: "b", isOpen: true, label: "On Hold" },
    { claimStatusCode: "bb", isOpen: false, label: "Claim Paid" },
    {
        claimStatusCode: "c",
        isOpen: true,
        label: "On Hold-Appraisal Company Needing Authorization to Inspect"
    },
    { claimStatusCode: "cc", isOpen: false, label: "Lender Request" },
    {
        claimStatusCode: "d",
        isOpen: true,
        label: "Pending Title Documentation from Lender"
    },
    { claimStatusCode: "dd", isOpen: false, label: "Supplement Paid" },
    {
        claimStatusCode: "e",
        isOpen: true,
        label: "Pending Information from Lender"
    },
    { claimStatusCode: "ee", isOpen: false, label: "No Reply" },
    {
        claimStatusCode: "f",
        isOpen: true,
        label: "Pending Documentation from Lender"
    },
    { claimStatusCode: "ff", isOpen: false, label: "No Recovery" },
    {
        claimStatusCode: "g",
        isOpen: true,
        label: "Pending Documentation from Borrower"
    },
    { claimStatusCode: "gg", isOpen: true, label: "Repo Agent Update" },
    {
        claimStatusCode: "h",
        isOpen: true,
        label: "Pending Documentation from Lender & Borrower"
    },
    {
        claimStatusCode: "hh",
        isOpen: true,
        label: "New Repossession Only Request"
    },
    {
        claimStatusCode: "i",
        isOpen: true,
        label: "Pending Location of Collateral"
    },
    { claimStatusCode: "ii", isOpen: false, label: "Recovery Payment Only" },
    {
        claimStatusCode: "j",
        isOpen: true,
        label: "Pending Coverage Review From ISI"
    },
    { claimStatusCode: "k", isOpen: true, label: "Pending Review From ISI" },
    {
        claimStatusCode: "l",
        isOpen: true,
        label: "Pending CPI Premium from Lender"
    },
    { claimStatusCode: "m", isOpen: true, label: "Claim Accepted" },
    { claimStatusCode: "n", isOpen: true, label: "Accepted as Courtesy Skip" },
    { claimStatusCode: "o", isOpen: true, label: "Damage Estimate Pending" },
    { claimStatusCode: "p", isOpen: true, label: "Supplement Pending" },
    { claimStatusCode: "q", isOpen: true, label: "Damage Estimate Update" },
    {
        claimStatusCode: "r",
        isOpen: true,
        label: "Estimate Received-Settlement Pending"
    },
    {
        claimStatusCode: "s",
        isOpen: true,
        label: "Estimate Received-Salvage Bids Pending"
    },
    { claimStatusCode: "t", isOpen: true, label: "Skip Update" },
    { claimStatusCode: "u", isOpen: false, label: "No Payment" },
    { claimStatusCode: "uu", isOpen: true, label: "Unsubmitted" },
    { claimStatusCode: "v", isOpen: false, label: "Repo Recovered" },
    { claimStatusCode: "w", isOpen: false, label: "Skip Recovered" },
    { claimStatusCode: "x", isOpen: false, label: "Skip Located" },
    { claimStatusCode: "y", isOpen: false, label: "Vehicle Surrendered" },
    { claimStatusCode: "z", isOpen: false, label: "Loan Paid Current" },
    { claimStatusCode: "fr", isOpen: true, label: "Pending Final Review" }
];

export enum ClaimStatus {
    Submitted = "a",
    OnHold = "b",
    OnHoldAppraisalCompanyNeedingAuthorizationToInspect = "c",
    PendingTitleDocumentationFromLender = "d",
    PendingInformationFromLender = "e",
    PendingDocumentationFromLender = "f",
    PendingDocumentationFromBorrower = "g",
    PendingDocumentationFromLenderAndBorrower = "h",
    PendingLocationOfCollateral = "i",
    PendingCoverageReviewFromISI = "j",
    PendingReviewFromISI = "k",
    PendingCPIPremiumFromLender = "l",
    OpenClaimAccepted = "m",
    OpenAcceptedAsCourtesySkip = "n",
    OpenDamageEstimatePending = "o",
    OpenSupplementPending = "p",
    OpenDamageEstimateUpdate = "q",
    OpenEstimateReceivedSettlementPending = "r",
    OpenEstimateReceivedSalvageBidsPending = "s",
    OpenSkipUpdate = "t",
    ClosedNoPayment = "u",
    ClosedRepoRecovered = "v",
    ClosedRecoveryPaymentOnly = "ii",
    ClosedSkipRecovered = "w",
    ClosedSkipLocated = "x",
    ClosedVehicleSurrendered = "y",
    ClosedLoanPaidCurrent = "z",
    ClosedClaimDenied = "aa",
    ClosedClaimPaid = "bb",
    ClosedLenderRequest = "cc",
    ClosedSupplementPaid = "dd",
    ClosedNoReply = "ee",
    ClosedNoRecover = "ff",
    RepoAgentUpdate = "gg",
    NewReposessionOnlyRequest = "hh",
    Unsubmitted = "uu",
    PendingFinalReview = "fr"
}

export type ClaimStatusDto = {
    claimStatusCode: string;
    name: string;
    isOpen: boolean;
};

export enum ClaimType {
    Bankruptcy = "B",
    Confiscation = "CF",
    Conversion = "CV",
    Hazard = "H",
    InstrumentNonFilingErrorsAndOmissions = "E",
    LienholderAndLossPayeeErrorAndOmissions = "ML",
    PremiumDeficiencyCoverageA = "PA",
    PremiumDeficiencyCoverageB = "PB",
    PrimaryDeductible = "PD",
    RepossessionDamage = "RD",
    RepossessionExpenseReimbursement = "R",
    RepossessionPropertyCoverage = "RV",
    Secretion = "S",
    Skip = "SK",
    Theft = "T",
    WaiverNonRepossessionDamage = "W"
}

const API_URL = environment.api;

export class ClaimFull {
    accountId: string;
    loanId: string;
    claimId: number;
    // borrower
    borrowerName: string;
    coBorrowerName: string;
    borrowerPhone1: string;
    borrowerPhone2: string;
    borrowerEmail: string;
    borrowerAddress1: string;
    borrowerAddress2: string;
    borrowerCity: string;
    borrowerState: string;
    borrowerZip: string;

    // Lender
    lenderName: string;
    lenderContactName: string;
    lenderContactPhone: string;
    lenderContactEmail: string;
    lenderNotes: string;

    // Loan
    programType: string;
    loanNumber: string;
    loanBalance: number;
    loanOpenDate: Date | string;
    productLine: string;

    // Collateral
    collateralId: string;
    collateralDescription: string;
    vin: string;

    // ISI Default Fields
    towingStorageExpensePaid: number;
    adminRepossessionExpensePaid: number;
    repossessionDeficiencyBalance: number;
    salvageValueAmount: number;
    subrogationCollectedAmount: number;
    lossReserveAmount: number;
    supplementPaidAmount: number;
    otherPaidAmount: number;
    deductibleAmount: number;
    claimPaidAmount: number;
    refundAmount: number;
    mechanicLienAmount: number;
    reportedDate: string;
    subrogationDate: Date | string;
    lossDate: string;
    paidDate: Date | string;
    claimNumber: string;
    policyNumber: string;
    masterPolicyNumber: string;
    settlementOption: string;
    claimProcessor: string;
    catastropheNumber: string;
    isException = false;
    denialReason: string;
    isHurricaneClaim = false;
    pastDueLimit: string;

    // ISI Conversion fields
    dueDate: Date | string;
    assignedTo: string;
    recoveredDate: Date | string;
    daysPastDue: string;
    notificationLimit: string;
    acvAmount: number;
    judgmentFiledDate: Date | string;

    // status and updates
    claimStatusCode: ClaimStatus;
    claimStatusUpdate: string;
    lenderDocsNeeded: string;
    borrowerDocsNeeded: string;

    // Vehicle Location
    vehicleLocationName: string;
    vehicleLocationAddress1: string;
    vehicleLocationAddress2: string;
    vehicleLocationCity: string;
    vehicleLocationState: string;
    vehicleLocationZip: string;
    vehicleLocationPhone: string;
    vehicleLocationContactName: string;

    // Claim Info
    claimTypeCode: ClaimType;
    loanSource: string;
    submittedBy: string;
    carrier: string | null;
    clientId: string;
    savedAt: Date;
    submittedAt: Date;
    createdAt?: Date;
    updatedAt?: Date;

    repossessionDate: Date | null;
    accidentDate: Date | null;
    locationOfDamages: string | null;

    /**
     * 0 = Unknown; 1 = Yes; 2 = No
     */
    isVehicleDrivable: 0 | 1 | 2;

    notificationDate: Date | null;
    theftDate: Date | null;
    theftLocation: string | null;
    cpiNotificationDate: Date | null;
    delinquencyDate: Date | null;
    loanPaidInFullDate: Date | null;
    collateralProceeds: number | null;
    claimPayment: number | null;
    repossessionExpensePaid: number | null;
    partialCpiRefundAmount: number | null;
    primaryInsuranceDeductible: number | null;
    repossessionExpenseAmount: number | null;
    possesionPreventedDate: Date | null;
    reliefOfStayDate: Date | null;

    // Hazard Claim Info
    discoveredDate: Date | null;
    lossType: string | null;
    isForeclosure: boolean | null;
    isVacant: boolean | null;

    // Claim Payment Info
    claimPaymentMethod: string | null;
    claimPaymentAddress: string | null;

    // Missing Lienholder Info
    primaryInsuranceCarrier: string | null;
    primaryInsuranceClaimNumber: string | null;
}

export interface IClaim {
    loanId: string;
    loanNumber: string;
    accountName: string;
    accountId: string;
    borrower: IAgent;
    borrowerName: string;
    claimId: string;
    claimTypeName: string;
    claimPaidAmount: number;
    claimStatusName: string;
    collateral: ICollateral;
    collateralId: string;
    collateralDescription: string;
    insuranceCoverageTypeCode: string;
    number: string;
    type: string;
    lossType: string;
    submittedAt: Date;
    updatedOn: Date;
    claimProcessor: string | null;
    denialReason: string | null;
    dueDate: Date | null;
    assignedTo: string | null;
    recoveredDate: Date | null;
    acvAmount: number | null;
    daysPastDue: string | null;
    pastDueLimit: string | null;
    isException: boolean;
    loanSource: string | null;
    claimDisbursementId: number | null;
    disbursementNumber: string | null;
    disbursedDate: Date | null;
}

export interface IClaimPagedResp {
    limit: number;
    offset: number;
    total: number;
    data: IClaim[];
}

export type ClaimNote = {
    claimId: number;
    noteId: number;
    text: string;
    createdBy: number;
    createdByString: string;
    createdAt: Date;
    createdAtString: string;
    updatedAt: Date;
    isPublic: boolean;
};

export class PolicyRecord {
    masterPolicyNumber: string;
    policyLetters: PolicyLetter[];

    static fromJSON(obj: any): PolicyRecord {
        let temp = new PolicyRecord();

        temp.masterPolicyNumber = obj.masterPolicyNumber;
        temp.policyLetters = obj.policyLetters.map((elem) =>
            PolicyLetter.fromJSON(elem)
        );

        return temp;
    }
}

@Injectable()
export class ClaimService {
    constructor(
        public http: HttpClient,
        private _authService: AuthService
    ) {}

    set newClaimData(obj) {
        this._newClaimData = obj;
        if (obj !== null) {
            let str = JSON.stringify(obj);
            localStorage.setItem("cpi_new_claim_data", str);
        } else {
            localStorage.removeItem("cpi_new_claim_data");
        }
    }

    get newClaimData() {
        if (this._newClaimData) {
            return this._newClaimData;
        } else {
            let str = localStorage.getItem("cpi_new_claim_data");
            if (str && str.length > 0) {
                this._newClaimData = JSON.parse(str);
                return this._newClaimData;
            } else {
                return null;
            }
        }
    }

    private _tempClaim: IClaim | null = null;
    get tempClaim(): IClaim | null {
        let _tempClaim = this._tempClaim;
        this._tempClaim = null;
        return _tempClaim;
    }
    set tempClaim(l: IClaim | null) {
        this._tempClaim = l;
    }

    private get _defaultOptions() {
        return { headers: this._headers };
    }
    private _newClaimData = null;
    private get _headers(): HttpHeaders {
        let h = new HttpHeaders();
        h = h.set("Authorization", `bearer ${this._authService.accessToken}`);
        h = h.set("Content-Type", "application/json");
        return h;
    }

    changes: Subject<any> = new BehaviorSubject(null);

    getAll(
        offset: number = 0,
        limit: number = 10,
        sort: string | null = "-claimId",
        search: string | null = null,
        accounts: string[] | null = null,
        statuses: string[] | null = null,
        types: string[] | null = null,
        startEnd: IStartEndDate | null = null,
        icc: boolean,
        paidDate: IStartEndDate | null = null
    ): Observable<IClaimPagedResp> {
        let p = {
            offset: `${offset * limit}`,
            limit: `${limit}`
        };

        if (search) {
            p["filter"] = search;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (statuses && statuses.length > 0) {
            p["statuses"] = statuses.join();
        }

        if (types && types.length > 0) {
            p["types"] = types.join();
        }

        if (startEnd && startEnd.start !== null) {
            p["startDate"] =
                startEnd.start.toISOString().split("T")[0] + "T00:00:00Z";
            p["endDate"] =
                startEnd.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (paidDate && paidDate.start !== null) {
            p["paidStartDate"] = paidDate.start.toISOString().split("T")[0];
            p["paidEndDate"] = paidDate.end.toISOString().split("T")[0];
        }

        if (icc) {
            p["closed"] = icc;
        }

        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            let sorts = sort.split(".");
            if (sorts.length > 1) {
                sort =
                    sorts[0] +
                    sorts[1].substr(0, 1).toUpperCase() +
                    sorts[1].substr(1, sorts[1].length);
            }
            p["sort"] = sort;
        } else {
            p["sort"] = "-claimId";
        }

        let options = { headers: this._headers, params: p };

        return this.http
            .get(`${API_URL}/api/v1/claims`, options)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    res.offset = res.offset / res.limit;
                    res.data = res.data.map(this._mapClaim);
                    return res;
                })
            );
    }

    exportClaims(
        columns,
        format,
        sort: string | null = null,
        search: string | null = null,
        accounts: string[] | null = null,
        statuses: string[] | null = null,
        types: string[] | null = null,
        startEnd: IStartEndDate | null = null,
        icc: boolean,
        paidStartEnd: IStartEndDate | null = null
    ): Observable<Blob> {
        let p = {
            format: format
        };

        if (search) {
            p["filter"] = search;
        }

        if (accounts && accounts.length > 0) {
            p["accounts"] = accounts.join(",");
        }

        if (statuses && statuses.length > 0) {
            p["statuses"] = statuses.join(",");
        }

        if (types && types.length > 0) {
            p["types"] = types.join();
        }

        if (startEnd && startEnd.start !== null) {
            p["startDate"] =
                startEnd.start.toISOString().split("T")[0] + "T00:00:00Z";
            p["endDate"] =
                startEnd.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (paidStartEnd && paidStartEnd.start !== null) {
            p["paidStartDate"] =
                paidStartEnd.start.toISOString().split("T")[0] + "T00:00:00Z";
            p["paidEndDate"] =
                paidStartEnd.end.toISOString().split("T")[0] + "T23:59:59.999Z";
        }

        if (icc) {
            p["closed"] = icc;
        }

        if (sort) {
            if (sort.charAt(0) === "+") {
                sort = sort.substr(1, sort.length);
            }
            let sorts = sort.split(".");
            if (sorts.length > 1) {
                sort =
                    sorts[0] +
                    sorts[1].substr(0, 1).toUpperCase() +
                    sorts[1].substr(1, sorts[1].length);
            }
            p["sort"] = sort;
        } else {
            p["sort"] = "-claimId";
        }

        let options = {
            headers: this._headers,
            params: p,
            responseType: "blob" as "json"
        };

        return this.http
            .post<Blob>(`${API_URL}/api/v1/claims/export`, columns, options)
            .pipe(
                map((res) => {
                    let url = window.URL.createObjectURL(res);
                    let a = document.createElement("a");
                    document.body.appendChild(a);
                    a.setAttribute("style", "display: none");
                    a.href = url;
                    a.download = buildExportFilename(
                        "claims_report",
                        this._authService.claims.accountName,
                        format
                    );
                    a.click();
                    window.URL.revokeObjectURL(url);
                    a.remove();

                    return res;
                })
            );
    }

    getClaim(id: string): Observable<ClaimFull> {
        return this.http
            .get(`${API_URL}/api/v1/claims/${id}`, this._defaultOptions)
            .pipe(map((res) => this._mapFullClaim(res)));
    }

    saveClaim(claim) {
        if (claim.claimId) {
            return this.http
                .put(
                    `${API_URL}/api/v1/claims/${claim.claimId}`,
                    claim,
                    this._defaultOptions
                )
                .pipe(map((res) => this._mapFullClaim(res)));
        } else {
            return this.http
                .post(`${API_URL}/api/v1/claims`, claim, this._defaultOptions)
                .pipe(map((res) => this._mapFullClaim(res)));
        }
    }

    deleteClaim(claimId) {
        return this.http.delete(
            `${API_URL}/api/v1/claims/${claimId}`,
            this._defaultOptions
        );
    }

    getFiles(claimId: number): Observable<any> {
        return this.http
            .get(
                `${API_URL}/api/v1/claims/${claimId}/files`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    // return this._mapClaim(res);
                    return res;
                })
            );
    }

    uploadFile(claimId: number, file: File): Observable<any> {
        let h = this._headers;
        h = h.set("Content-Type", "multipart/form-data");
        h = h.delete("Content-Type");
        let options = { headers: h };
        let formData = new FormData();
        formData.append("file", file, file.name);
        return this.http
            .post(
                `${API_URL}/api/v1/claims/${claimId}/files`,
                formData,
                options
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    deleteFile(claimId: number, fileId: number): Observable<any> {
        return this.http
            .delete(
                `${API_URL}/api/v1/claims/${claimId}/files/${fileId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    // return this._mapClaim(res);
                    return res;
                })
            );
    }

    notifyFileUpload(claimId: number, fileNames: string[]): Observable<any> {
        return this.http.post(
            `${API_URL}/api/v1/claims/${claimId}/files/notify`,
            { fileNames },
            this._defaultOptions
        );
    }

    getFileRequirements(claimId: number): Observable<any> {
        return this.http
            .get(
                `${API_URL}/api/v1/claims/${claimId}/file/requirements`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    removeFileRequirement(claimId: number, fileType: string): Observable<any> {
        return this.http
            .delete(
                `${API_URL}/api/v1/claims/${claimId}/file/requirements/${fileType}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    addFileRequirement(claimId: number, fileType: string): Observable<any> {
        let obj = {
            claimId: claimId,
            fileType: fileType,
            isCertified: false,
            isRejected: false
        };
        return this.http
            .post(
                `${API_URL}/api/v1/claims/${claimId}/file/requirements/add`,
                obj,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editFileRequirement(
        claimId: number,
        fileType: string,
        certified: boolean,
        rejected: boolean,
        checked: boolean
    ): Observable<any> {
        let obj = {
            claimId: claimId,
            fileType: fileType,
            isCertified: certified,
            isRejected: rejected,
            isChecked: checked
        };
        return this.http
            .post(
                `${API_URL}/api/v1/claims/${claimId}/file/requirements/edit`,
                obj,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getFileURL(claimId: number, fileId: number): Observable<string> {
        let h = this._headers;
        let options = { headers: h, responseType: "text" as "json" };
        return this.http.get<string>(
            `${API_URL}/api/v1/claims/${claimId}/files/${fileId}/url`,
            options
        );
    }

    getClaimOptions(fieldName: string): Observable<any[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/claims/options/${fieldName}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getClaimSpecialists() {
        return this.http.get<NameValue[]>(
            `${API_URL}/api/v1/claims/specialist-options`,
            this._defaultOptions
        );
    }

    getClaimStatuses(): Observable<ClaimStatusDto[]> {
        return this.http
            .get(`${API_URL}/api/v1/claim-statuses`, this._defaultOptions)
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getClaimProcessorInfo(claimId: number): Observable<any> {
        return this.http
            .get(
                `${API_URL}/api/v1/claims/options/processor-info/${claimId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res ?
                            {
                                name: `${res.firstName} ${res.lastName}`,
                                phone: res.phone,
                                email: res.email
                            }
                        :   null;
                })
            );
    }

    getClaimPolicyNumberOptions(claimId: number): Observable<string[]> {
        return this.http
            .get<
                string[]
            >(`${API_URL}/api/v1/claims/options/policy-numbers/${claimId}`, this._defaultOptions)
            .pipe(shareReplay());
    }

    getPolicyRecords(policyNumber: string): Observable<PolicyRecord> {
        return this.http
            .get<PolicyRecord>(
                `${API_URL}/api/v1/claims/options/policy-records/${policyNumber}`,
                this._defaultOptions
            )
            .pipe(
                shareReplay(),
                map((res: any) => PolicyRecord.fromJSON(res))
            );
    }

    getClaimNotes(claimId: number): Observable<ClaimNote[]> {
        return this.http
            .get(
                `${API_URL}/api/v1/claims/${claimId}/notes`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res.map(this._mapClaimNote);
                })
            );
    }

    addClaimNote(
        claimId: number,
        note: string,
        isPublic: boolean
    ): Observable<ClaimNote> {
        return this.http
            .post(
                `${API_URL}/api/v1/claims/${claimId}/notes?isPublic=${isPublic}`,
                JSON.stringify(note),
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return this._mapClaimNote(res);
                })
            );
    }

    editClaimNote(
        claimId: number,
        noteId: number,
        note: string,
        isPublic: boolean
    ): Observable<ClaimNote> {
        return this.http
            .put(
                `${API_URL}/api/v1/claims/${claimId}/notes/${noteId}?isPublic=${isPublic}`,
                JSON.stringify(note),
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return this._mapClaimNote(res);
                })
            );
    }

    deleteClaimNote(claimId: number, noteId: number): Observable<ClaimNote> {
        return this.http
            .delete(
                `${API_URL}/api/v1/claims/${claimId}/notes/${noteId}`,
                this._defaultOptions
            )
            .pipe(shareReplay())
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    listCannedAttachments(claimId: number): Observable<string[]> {
        return this.http.get<string[]>(
            `${API_URL}/api/v1/claims/${claimId}/files/canned-attachments`,
            this._defaultOptions
        );
    }

    sendContactCustomerNotification(
        claimId: number,
        subject: string,
        body: string,
        fileAttachmentIds: number[],
        cannedAttachmentFileNames: string[]
    ) {
        const model = {
            subject,
            body,
            fileAttachmentIds,
            cannedAttachmentFileNames
        };

        return this.http.post(
            `${API_URL}/api/v1/claims/${claimId}/contact-customer`,
            model,
            this._defaultOptions
        );
    }

    private _mapClaimNote(c): ClaimNote {
        const claimNote = { ...c } as ClaimNote;

        if (c.createdAt) {
            // The + 'Z' forces the createdAt date to be read as UTC
            claimNote.createdAt = new Date(c.createdAt + "Z");
        }

        if (c.updatedAt) {
            claimNote.updatedAt = new Date(c.updatedAt);
        }

        return claimNote;
    }

    private _mapFullClaim(c): ClaimFull {
        const claimFull = { ...c } as ClaimFull;
        claimFull.programType = c.programType;

        if (c.savedAt) {
            claimFull.savedAt = new Date(c.savedAt);
        }

        if (c.submittedAt) {
            claimFull.submittedAt = new Date(c.submittedAt);
        }

        if (c.createdAt) {
            claimFull.createdAt = new Date(c.createdAt);
        }

        if (c.updatedAt) {
            claimFull.updatedAt = new Date(c.updatedAt);
        }

        if (c.repossessionDate) {
            claimFull.repossessionDate = new Date(c.repossessionDate);
        }

        if (c.accidentDate) {
            claimFull.accidentDate = new Date(c.accidentDate);
        }

        if (c.notificationDate) {
            claimFull.notificationDate = new Date(c.notificationDate);
        }

        if (c.theftDate) {
            claimFull.theftDate = new Date(c.theftDate);
        }

        if (c.cpiNotificationDate) {
            claimFull.cpiNotificationDate = new Date(c.cpiNotificationDate);
        }

        if (c.delinquencyDate) {
            claimFull.delinquencyDate = new Date(c.delinquencyDate);
        }

        if (c.loanPaidInFullDate) {
            claimFull.loanPaidInFullDate = new Date(c.loanPaidInFullDate);
        }

        if (c.possesionPreventedDate) {
            claimFull.possesionPreventedDate = new Date(
                c.possesionPreventedDate
            );
        }

        if (c.reliefOfStayDate) {
            claimFull.reliefOfStayDate = new Date(c.reliefOfStayDate);
        }

        if (c.loanOpenDate) {
            claimFull.loanOpenDate = new Date(c.loanOpenDate);
        }

        if (c.dueDate) {
            claimFull.dueDate = new Date(c.dueDate);
        }

        if (c.subrogationDate) {
            claimFull.subrogationDate = new Date(c.subrogationDate);
        }

        if (c.recoveredDate) {
            claimFull.recoveredDate = new Date(c.recoveredDate);
        }

        if (c.judgmentFiledDate) {
            claimFull.judgmentFiledDate = new Date(c.judgmentFiledDate);
        }

        return claimFull;
    }

    private _mapClaim(l: any): IClaim {
        let temp = l as IClaim;

        temp.type = l.claimType;
        temp.accountId = temp.accountId.replace(/0+/, "");

        if (l.submittedAt) {
            temp.submittedAt = new Date(l.submittedAt);
        }

        let a: IAgent = {} as IAgent;
        a.name = l.borrowerName;
        a.phone1 = l.borrowerPhone1;
        a.phone2 = l.borrowerPhone2;
        a.phone2 = l.borrowerPhone3;
        a.email = l.borrowerEmail;
        a.address1 = l.borrowerAddress1;
        a.address2 = l.borrowerAddress2;
        a.city = l.borrowerCity;
        a.state = l.borrowerState;
        a.zip = l.borrowerZip;
        temp.borrower = a;

        let collateral: ICollateral = {} as ICollateral;
        collateral.collateralId = l.collateralId;
        collateral.collateralDescription = l.collateralDescription;
        collateral.collateralYear = l.collateralYear;

        temp.collateral = collateral;

        if (l.updatedOn) {
            temp.updatedOn = new Date(l.updatedOn);
        }

        if (l.dueDate) {
            temp.dueDate = new Date(l.dueDate);
        }

        if (l.recoveredDate) {
            temp.recoveredDate = new Date(l.recoveredDate);
        }

        return temp;
    }
}
